import { useState, useEffect } from 'react';
import { fmtMoney, fmtNumber } from '@/lib/format';
import { SectionHeader, StatusBadge, FilterPills, PremiumTable, PageSkeleton, LoadError } from '@/components/dashboard/MetricCard';
import { dashboardApi } from '@/services/dashboardApi';
import { motion } from 'framer-motion';

/*
  Actividad comercial (solo brokers Premium) — datos reales del CDM (08/10/2026): altas que patrocinas,
  upgrades de esos socios y aportaciones donde eres N1. «Pendiente» = aún no ha pagado el 100 %;
  «Reserva» = plaza reservada, todavía no es alta.
  Hasta la licencia suiza no se habla de tokens en el panel: son «aportaciones» con su importe (09/10/2026).
  Ruta: /dashboard/actividad (la antigua /dashboard/tokens redirige aquí).
*/
const STATUSES = ['Todas', 'Completada', 'Pendiente', 'Reserva', 'Baja'];

export default function DashboardActividad() {
  const [sales, setSales] = useState([]);
  const [filter, setFilter] = useState('Todas');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setLoading(true);
    setError(null);
    dashboardApi.sales(filter === 'Todas' ? null : filter)
      .then((r) => { setSales(r); setError(null); })
      .catch((e) => { console.error(e); setError(e.message || 'Error'); })
      .finally(() => setLoading(false));
  }, [filter, attempt]);

  const total = sales.filter((s) => ['Completada', 'Pendiente'].includes(s.status)).reduce((a, s) => a + (s.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="dashboard-actividad">
      <SectionHeader title="Actividad comercial" subtitle="Membresías, upgrades y aportaciones de los clientes que has traído al club" />
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <FilterPills options={STATUSES} active={filter} onChange={setFilter} />
        {!loading && !error && sales.length > 0 && (
          <p className="text-xs text-gray-400">{fmtNumber(sales.length)} operaciones · <span className="text-white font-medium">{fmtMoney(total, 'eur')}</span> en curso o completadas</p>
        )}
      </div>
      {loading ? (
        <PageSkeleton cards={0} rows={6} />
      ) : error ? (
        <LoadError onRetry={() => setAttempt((n) => n + 1)} />
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
          <PremiumTable
            cols={['Cliente', 'Operación', 'Importe', 'Pagado', 'Fecha', 'Estado']}
            emptyText={filter === 'Todas' ? 'Todavía no hay operaciones. Cuando un contacto tuyo se haga socio con tu enlace aparecerá aquí.' : `No hay operaciones con estado «${filter}».`}
            rows={sales.map((s) => [
              s.client || '—',
              s.kind === 'aportacion' ? <span className="text-[#c9a84c] font-medium">Aportación</span> : s.property_name,
              s.kind === 'membership' && !s.amount ? <span className="text-gray-400" title="Membresía incluida en otra operación (sin precio propio)">Incluida</span> : fmtMoney(s.amount, s.currency || 'eur'),
              s.status === 'Reserva' ? '—' : fmtMoney(s.paid_amount ?? 0, s.currency || 'eur'),
              s.date ? `${s.date.slice(8, 10)}/${s.date.slice(5, 7)}/${s.date.slice(0, 4)}` : '—',
              <StatusBadge status={s.status} />,
            ])}
          />
          <p className="text-[11px] text-gray-500 mt-3">Importes en euros. «Pendiente»: el socio aún no ha completado el pago. «Reserva»: plaza reservada que todavía no es alta.</p>
        </motion.div>
      )}
    </div>
  );
}
